
/*
 * This file is a custom implementation of the TranslateLoader interface that provides translations for different languages.
 * It is used by the TranslateModule to fetch translations for the application.
 * All translations are stored in JSON files in the assets/i18n directory.
 * A translation object is created for each language and exported as a module.
 * The translation objects are then imported and stored in a constant object that maps language codes to translation objects.
 * The StaticTranslationLoader class fetches the translation object for a given language from the constant object which is faster then loading via http.
 * https://www.codeandweb.com/babeledit/tutorials/how-to-translate-your-angular-app-with-ngx-translate#install-common
 */

import { TranslateLoader } from "@ngx-translate/core";
import { Observable, of } from "rxjs";

import * as TranslationFR from "src/assets/i18n/fr.json";
import * as TranslationEN from "src/assets/i18n/en.json";


/**
 * Represents a translation object where keys are strings and values can be either strings or nested translation objects.
 * This allows for hierarchical translation structures.
 */
interface Translation {
  [key: string]: string | Translation;
}
/**
 * A constant object that holds translations for different languages.
 *
 * @constant
 * @type {Translation}
 * @property {Translation} fr - The French translations.
 * @property {Translation} en - The English translations.
 */
const TRANSLATIONS: Translation = {
  fr: TranslationFR,
  en: TranslationEN,
};

/*
 * StaticTranslationLoader is a custom implementation of the TranslateLoader interface.
 * It provides a method to fetch translations for a given language.
 */
export class StaticTranslationLoader implements TranslateLoader {
  /**
   * Fetches the translation for the specified language.
   *
   * @param lang - The language code for which the translation is requested.
   * @returns An Observable that emits the translation object or an empty object if no translation is found.
   */
  public getTranslation(lang: string): Observable<Translation | {}> {
    const translation = TRANSLATIONS[lang];
    if (translation) {
      return of(translation);
    } else {
      console.error(`No translation found for lang: ${lang}`);
      return of({});
    }
  }
}
